import { useState } from "react";
import { useEscalationRate, useWorkshopAttention, useWorkshopTat } from "@/lib/api";
import { AN_CARD, AN_H, AN_NUM, AN_SUB, AN_T, Unit } from "@/components/msd/analytics";
import { hoursToHM } from "@/lib/msdAnalytics";
import { formatPct } from "../../lib/workshopAnalytics";
import { advisorRows, sortRows, summarize } from "../../lib/advisorPerformance";
import AdvisorDetail from "./AdvisorDetail";

// Service advisor performance: one row per advisor, compared side by side. Only
// managers get here (the page checks). The page hands over the date range and
// locations; the figures are fetched here, so they are only asked for while this
// tab is open. Clicking an advisor opens their details in a side panel.

const COLUMNS = [
  ["name", "Advisor", "text-left"],
  ["groups", "Service groups", "text-right"],
  ["unanswered", "Unanswered", "text-right"],
  ["notJoined", "Customers not joined", "text-right"],
  ["escalationPct", "Escalated", "text-right"],
];

const TH = "whitespace-nowrap border-0 border-b border-solid border-msd-line p-0 text-[12.5px] font-semibold text-msd-muted";
const TD = "whitespace-nowrap border-0 border-b border-solid border-msd-line px-3 py-[10px]";

const HM = ({ hours }) => {
  const v = hoursToHM(hours);
  if (!v) return "—";
  return (
    <>
      {v.h}
      <Unit>h</Unit> {v.m}
      <Unit>m</Unit>
    </>
  );
};

function Tile({ label, hint, testid, children }) {
  return (
    <div className={`${AN_CARD} !p-5`} data-testid={testid} title={hint}>
      <div className={AN_SUB}>{label}</div>
      <div className={AN_NUM}>{children}</div>
    </div>
  );
}

export default function AdvisorPerformance({ base, scopeLabel, workshopLabel = (x) => x, onOpenUnanswered, onOpenGroup }) {
  const [sort, setSort] = useState({ key: "groups", dir: "desc" });
  const [selected, setSelected] = useState(null);
  const range = base && { ...base, by: "advisor" };
  const attention = useWorkshopAttention(base);
  const tat = useWorkshopTat(range);
  const escalation = useEscalationRate(range);

  const others = [tat, escalation];
  if (attention.isError) {
    return (
      <div className={`${AN_CARD} text-center`}>
        <div className="text-[20px] font-semibold">Couldn&apos;t load the numbers</div>
      </div>
    );
  }
  if (!attention.data || others.some((q) => q.isLoading)) return null;

  const rows = advisorRows({
    attention: attention.data,
    tat: tat.data,
    escalation: escalation.data,
  });
  const totals = summarize(rows, { tat: tat.data, escalation: escalation.data });
  const sorted = sortRows(rows, sort.key, sort.dir);
  // Name reads A to Z first; every figure starts with the biggest number.
  const pick = (key) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === "desc" ? "asc" : "desc") : key === "name" ? "asc" : "desc" }));

  return (
    <>
      <div className="grid grid-cols-4 gap-4 max-[1150px]:grid-cols-2 max-[640px]:grid-cols-1">
        <Tile label="Advisors with groups" testid="msd-adv-advisors" hint="Advisors who had at least one service group in this period">
          {totals.advisors}
        </Tile>
        <Tile label="Average repair TAT" testid="msd-adv-tat" hint="Average time from the group being created to the car being ready">
          <HM hours={totals.repairHours} />
        </Tile>
        <Tile label="Escalation rate" testid="msd-adv-escalation" hint="Share of service groups that were escalated">
          {totals.escalationPct == null ? (
            "—"
          ) : (
            <>
              {totals.escalationPct}
              <Unit>%</Unit>
            </>
          )}
        </Tile>
        <Tile label="Unanswered messages" testid="msd-adv-unanswered" hint="Service groups whose latest customer message is still waiting for a reply">
          {totals.unanswered}
        </Tile>
      </div>

      <section className={AN_CARD} data-testid="msd-advisor-leaderboard">
        <div className={AN_H}>
          <div>
            <div className={AN_T}>Advisor leaderboard · {scopeLabel}</div>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className={`${AN_SUB} py-8 text-center !text-[15px]`}>No advisors have service groups in this period.</div>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[760px] border-separate border-spacing-0 text-[14.5px]">
              <thead>
                <tr>
                  {COLUMNS.map(([key, label, align]) => {
                    const on = sort.key === key;
                    return (
                      <th key={key} scope="col" aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"} className={TH}>
                        <button
                          type="button"
                          onClick={() => pick(key)}
                          data-testid={`msd-adv-sort-${key}`}
                          className={`w-full cursor-pointer border-0 bg-transparent px-3 py-[10px] font-[inherit] hover:text-msd-ink ${align} ${on ? "text-msd-ink" : ""}`}
                        >
                          {label}
                          {on ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.key} data-testid="msd-adv-row">
                    <th scope="row" className={`${TD} text-left font-medium`}>
                      <button
                        type="button"
                        onClick={() => setSelected(r)}
                        data-testid="msd-adv-open"
                        title="Open this advisor's groups"
                        className="cursor-pointer border-0 bg-transparent p-0 text-left font-[inherit] font-medium text-msd-ink hover:underline"
                      >
                        {r.name}
                      </button>
                      <span className={`${AN_SUB} block font-normal`}>
                        {[r.workshop && workshopLabel(r.workshop), r.duplicate && "second number"].filter(Boolean).join(" · ")}
                      </span>
                    </th>
                    <td className={`${TD} text-right tabular-nums`}>{r.groups}</td>
                    <td className={`${TD} text-right tabular-nums`}>
                      {r.unanswered > 0 ? (
                        <button
                          type="button"
                          onClick={() => onOpenUnanswered(r)}
                          title="Open their unanswered chats"
                          className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-[#3A5F94] underline"
                        >
                          {r.unanswered}
                        </button>
                      ) : (
                        0
                      )}
                    </td>
                    <td className={`${TD} text-right tabular-nums`}>{r.notJoined}</td>
                    <td className={`${TD} text-right tabular-nums`}>{formatPct(r.escalationPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={`${AN_SUB} mt-3`}>
          {others.some((q) => q.isError) && " Some figures couldn't be loaded and show a dash."}
        </div>
      </section>

      {selected && (
        <AdvisorDetail
          advisor={selected}
          base={base}
          workshopLabel={workshopLabel}
          onClose={() => setSelected(null)}
          onOpenGroup={onOpenGroup}
        />
      )}
    </>
  );
}
